import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import Button from "../../components/Button";
import Input from "../../components/Input";
import Icon from "../../components/Icon";

import api from "../../services/api";
import { renderError } from "../../helpers/errors";
import {
    generateBrandSlug,
    getBrandsProvisionalDomain,
    getUrlValueMaxLength,
    sanitizeUrlValue,
    splitCustomHost,
    stripBrandsProvisionalDomain,
    validateCustomHost,
    BRAND_CNAME_TARGET
} from "../../helpers/brand";

const DNS_RECHECK_MS = 60000;
const DNS_MANUAL_URL = 'https://ajuda.brinde.me/t/base-de-conhecimento';

// O host que o "Ativar domínio" coloca no ar. Marca salva antes do
// pending_domain existir tem o próprio em `domain`, ainda inativo.
const hostToActivate = brand => brand?.pending_domain
    || (brand?.is_custom_domain && !brand?.active ? brand?.domain : null);

const formatDate = iso => iso ? new Date(iso).toLocaleDateString('pt-BR') : null;
const formatTime = date => date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

export const DomainTab = ({ brand, canEdit, onChange }) => {
    const pendingHost = hostToActivate(brand);
    const liveCustom = Boolean(brand?.is_custom_domain && brand?.active && !pendingHost);
    const servingProvisional = !brand?.is_custom_domain && brand?.active;

    const [mode, setMode] = useState(pendingHost || brand?.is_custom_domain ? 'custom' : 'provisional');
    const [label, setLabel] = useState(
        brand?.is_custom_domain ? generateBrandSlug(brand?.name) : stripBrandsProvisionalDomain(brand?.domain)
    );
    const [draft, setDraft] = useState(pendingHost ?? (brand?.is_custom_domain ? brand?.domain : '') ?? '');
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [activating, setActivating] = useState(false);
    const [status, setStatus] = useState(null);
    const [checking, setChecking] = useState(false);
    const [checkedAt, setCheckedAt] = useState(null);

    // Sem domínio próprio salvo não há o que mostrar como concluído no passo 1.
    const isEditing = editing || (!pendingHost && !liveCustom);
    const checkedHost = pendingHost ?? (liveCustom ? brand.domain : null);
    const dnsOk = status?.host === checkedHost && status?.dns_status === 'connected';
    const cnameTarget = status?.cname_target || BRAND_CNAME_TARGET;
    const found = status?.host === pendingHost ? status?.dns_found : null;
    const foundCname = found?.cname?.replace(/\.$/, '');
    // CNAME certo que ainda não resolve para o nosso IP é propagação, não erro.
    const dnsWrong = !dnsOk && (foundCname ? foundCname !== cnameTarget : found?.ips?.length > 0);

    // A verificação automática falha calada: com o DNS instável viraria um
    // toast de erro por minuto.
    const checkDns = (silent = false) => {
        setChecking(true);

        api.get(`/brand/${brand.id}/dns-status`).then(res => {
            setStatus(res.data);
            setCheckedAt(new Date());
        }).catch(error => {
            if (!silent) renderError(error);
        }).then(() => setChecking(false));
    };

    useEffect(() => {
        if (!checkedHost) {
            setStatus(null);
            return;
        }

        checkDns();
    }, [checkedHost]);

    // Enquanto o apontamento não aparece a tela confere sozinha: a propagação
    // do DNS leva de minutos a horas e ninguém fica apertando "verificar".
    useEffect(() => {
        if (!pendingHost || dnsOk) return;

        const timer = setInterval(() => checkDns(true), DNS_RECHECK_MS);
        return () => clearInterval(timer);
    }, [pendingHost, dnsOk]);

    const saveDomain = (custom, domain) => {
        setSaving(true);

        api.put(`/brand/${brand.id}/domain`, { is_custom_domain: custom, domain }).then(res => {
            const resource = res.data.resource;
            onChange(resource);
            setEditing(false);
            setDraft(resource.pending_domain ?? (resource.is_custom_domain ? resource.domain : ''));
            if (!custom) {
                toast.success('Endereço provisório salvo.');
            } else {
                toast.success(resource.pending_domain ? 'Domínio salvo. Agora aponte o DNS.' : 'Troca de domínio cancelada.');
            }
        }).catch(error => {
            renderError(error);
        }).then(() => setSaving(false));
    };

    const activateDomain = () => {
        setActivating(true);

        api.post(`/brand/${brand.id}/domain/activate`).then(res => {
            onChange(res.data.resource);
            toast.success('Domínio ativado. O site da marca já está no ar.');
            // O host verificado é o mesmo antes e depois, então o efeito não
            // roda de novo; sem isto a validade do certificado só viria no F5.
            checkDns(true);
        }).catch(error => {
            renderError(error);
        }).then(() => setActivating(false));
    };

    const copy = value => {
        navigator.clipboard?.writeText(value).then(() => toast.success('Copiado'));
    };

    const renderModes = () => (
        <div className="brand-domain-modes" role="radiogroup" aria-label="Endereço do site">
            {[
                {
                    value: 'provisional',
                    title: 'Endereço da Brinde.me',
                    text: 'Pronto na hora, sem configurar nada.',
                    tag: `${label || 'suamarca'}.${getBrandsProvisionalDomain()}`
                },
                {
                    value: 'custom',
                    title: 'Domínio próprio',
                    text: 'Ex.: loja.suamarca.com.br. Requer um ajuste no DNS.'
                }
            ].map(option => (
                <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={mode === option.value}
                    className={`brand-domain-mode${mode === option.value ? ' active' : ''}`}
                    onClick={() => setMode(option.value)}
                    disabled={!canEdit}
                >
                    <span className="brand-theme-radio" />
                    <span>
                        <strong>{option.title}</strong>
                        <small>{option.text}</small>
                        {option.tag && <code>{option.tag}</code>}
                    </span>
                </button>
            ))}
        </div>
    );

    const renderLive = (host, detail) => (
        <div className="brand-domain-live">
            <div>
                <strong>No ar</strong>
                <code>https://{host}</code>
                {detail && <small>{detail}</small>}
            </div>
            <a className="brand-domain-open" href={`https://${host}`} target="_blank" rel="noopener noreferrer">
                Abrir site <Icon name="external-link" size={14} />
            </a>
        </div>
    );

    const renderProvisional = () => {
        const host = `${label}.${getBrandsProvisionalDomain()}`;
        const unchanged = servingProvisional && host === brand?.domain && !pendingHost;

        return (
            <>
                {unchanged && renderLive(brand.domain)}

                {canEdit &&
                    <div className="brand-domain-field">
                        <div className="brand-url-field">
                            <Input
                                type={`text`}
                                label={`Endereço`}
                                value={label}
                                change={value => setLabel(sanitizeUrlValue(false, value).slice(0, getUrlValueMaxLength(false)))}
                                right={<span className="brand-url-suffix">.{getBrandsProvisionalDomain()}</span>}
                            />
                        </div>
                        <Button
                            type={`primary`}
                            size={`small`}
                            text={brand?.is_custom_domain || pendingHost ? 'Usar este endereço' : 'Salvar endereço'}
                            action={() => saveDomain(false, host)}
                            loading={saving}
                            disabled={!label || unchanged}
                        />
                    </div>
                }

                {(brand?.is_custom_domain || pendingHost) &&
                    <p className="section-hint">
                        {[
                            brand?.is_custom_domain && brand?.active && `O site sai de ${brand.domain}.`,
                            pendingHost && pendingHost !== brand?.domain && `O domínio ${pendingHost}, ainda não ativado, é descartado.`,
                            'O site passa a abrir neste endereço na hora.'
                        ].filter(Boolean).join(' ')}
                    </p>
                }
            </>
        );
    };

    const renderStep = (number, state, title, meta, body) => (
        <li className={`brand-domain-step is-${state}`}>
            <span className="brand-domain-step-number">
                {state === 'done' ? <Icon name="check" size={16} strokeWidth={3} /> : state === 'error' ? '!' : number}
            </span>
            <div className="brand-domain-step-content">
                <h3>{title}</h3>
                {meta && <p className="brand-domain-step-meta">{meta}</p>}
                {state !== 'locked' && body}
            </div>
        </li>
    );

    const renderHostStep = () => {
        if (!isEditing) {
            const savedHost = pendingHost ?? brand.domain;

            return renderStep(1, 'done', (
                <span className="brand-domain-saved">
                    <code>{savedHost}</code>
                    {canEdit &&
                        <button type="button" className="brand-domain-link" onClick={() => { setDraft(savedHost); setEditing(true); }}>
                            Alterar
                        </button>
                    }
                </span>
            ));
        }

        const error = validateCustomHost(draft);
        const unchanged = draft === pendingHost || (liveCustom && draft === brand.domain);
        const canCancel = Boolean(pendingHost || liveCustom);

        return renderStep(1, 'current', 'Qual domínio você quer usar?', null, (
            <>
                <div className="brand-domain-field">
                    <Input
                        type={`text`}
                        label={`Domínio`}
                        value={draft}
                        change={value => setDraft(sanitizeUrlValue(true, value).slice(0, getUrlValueMaxLength(true)))}
                        left={<span className="brand-domain-prefix">https://</span>}
                        error={draft ? error : null}
                        hint={`Use um subdomínio que você controla, como marca.seudominio.com.br.`}
                    />
                    <Button
                        type={`primary`}
                        size={`small`}
                        text={`Salvar domínio`}
                        action={() => saveDomain(true, draft)}
                        loading={saving}
                        disabled={!draft || Boolean(error) || unchanged}
                    />
                    {canCancel &&
                        <button type="button" className="brand-domain-link" onClick={() => { setDraft(pendingHost ?? brand.domain); setEditing(false); }}>
                            Cancelar
                        </button>
                    }
                </div>
            </>
        ));
    };

    const renderCheck = () => {
        if (dnsOk) {
            return (
                <div className="brand-domain-check is-ok">
                    <Icon name="check" size={18} strokeWidth={2.5} />
                    <div>
                        Apontamento encontrado
                        <small>{pendingHost} → {foundCname || cnameTarget}</small>
                    </div>
                </div>
            );
        }

        const recheck = (
            <button type="button" className="brand-domain-link" onClick={checkDns} disabled={checking}>
                {checking ? 'Verificando...' : 'Verificar agora'}
            </button>
        );

        if (dnsWrong) {
            return (
                <div className="brand-domain-check is-error">
                    <Icon name="close" size={18} strokeWidth={2.5} />
                    <div>
                        O domínio aponta para outro lugar
                        <small>
                            Encontramos <code>{foundCname || found.ips.join(', ')}</code>.
                            {' '}Troque o registro para um CNAME apontando para <code>{cnameTarget}</code>.
                        </small>
                    </div>
                    {recheck}
                </div>
            );
        }

        return (
            <div className="brand-domain-check is-waiting">
                <span className="brand-domain-pulse" />
                <div>
                    Procurando o apontamento...
                    <small>
                        Verificamos sozinhos a cada minuto
                        {checkedAt && ` · última verificação às ${formatTime(checkedAt)}`}
                        {' '}· a propagação costuma levar de minutos a algumas horas
                    </small>
                </div>
                {recheck}
            </div>
        );
    };

    const renderDnsStep = () => {
        if (!pendingHost) {
            return liveCustom
                ? renderStep(2, 'done', 'Domínio apontado para a Brinde.me')
                : renderStep(2, 'locked', 'Aponte o domínio para a Brinde.me', 'Disponível depois de salvar o domínio.');
        }

        const { name, zone } = splitCustomHost(pendingHost);

        return renderStep(2, dnsOk ? 'done' : dnsWrong ? 'error' : 'current', 'Aponte o domínio para a Brinde.me',
            dnsOk ? null : 'Crie o registro abaixo no seu provedor de DNS (Registro.br, GoDaddy, Cloudflare...).',
            <>
                {!dnsOk &&
                    <div className="brand-domain-dns">
                        <table>
                            <thead>
                                <tr><th>Tipo</th><th>Nome</th><th>Valor / destino</th></tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><code>CNAME</code></td>
                                    <td>
                                        <button type="button" className="brand-domain-copy" onClick={() => copy(name)}>
                                            <code>{name}</code> <Icon name="copy" size={14} />
                                        </button>
                                    </td>
                                    <td>
                                        <button type="button" className="brand-domain-copy" onClick={() => copy(cnameTarget)}>
                                            <code>{cnameTarget}</code> <Icon name="copy" size={14} />
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <div className="brand-domain-dns-foot">
                            <span>Crie este registro no painel onde o domínio <strong>{zone}</strong> está registrado.</span>
                            <a href={DNS_MANUAL_URL} target="_blank" rel="noopener noreferrer">Passo a passo por provedor →</a>
                        </div>
                    </div>
                }
                {renderCheck()}
            </>
        );
    };

    const renderActivateStep = () => {
        if (liveCustom) {
            const expires = formatDate(status?.host === brand.domain ? status?.ssl_expires_at : null);

            return renderStep(3, 'done', 'Site no ar', null,
                renderLive(brand.domain, expires ? `Certificado de segurança válido até ${expires}` : null)
            );
        }

        if (!pendingHost || !dnsOk) {
            return renderStep(3, 'locked', 'Coloque no ar', 'Disponível quando o apontamento for encontrado.');
        }

        return renderStep(3, 'current', 'Coloque no ar', null, (
            <div className="brand-domain-activate">
                {canEdit &&
                    <Button
                        type={`primary`}
                        size={`small`}
                        text={activating ? 'Ativando...' : 'Ativar domínio'}
                        action={activateDomain}
                        loading={activating}
                    />
                }
                <p>
                    {activating
                        ? 'Emitindo o certificado de segurança. Leva alguns segundos, não feche a tela.'
                        : `Emitimos o certificado de segurança (https) e o site passa a abrir em ${pendingHost}.`
                            + (brand.active && brand.domain !== pendingHost ? ` O endereço ${brand.domain} deixa de funcionar.` : '')}
                </p>
            </div>
        ));
    };

    const renderCustom = () => (
        <>
            {!liveCustom && brand?.active &&
                <p className="brand-domain-note">
                    <Icon name="link" size={16} />
                    <span>Enquanto o domínio novo não for ativado, o site continua no ar em <strong>{brand.domain}</strong>.</span>
                </p>
            }

            <ol className="brand-domain-steps">
                {renderHostStep()}
                {renderDnsStep()}
                {renderActivateStep()}
            </ol>
        </>
    );

    return (
        <div className="brand-overview brand-domain">
            <section className="brand-section">
                <h2 className="section-title">Endereço do site</h2>
                <p className="section-hint">Onde os clientes acessam o site da marca.</p>

                {renderModes()}

                {mode === 'provisional' ? renderProvisional() : renderCustom()}
            </section>
        </div>
    );
};

export default DomainTab;
